import React, { useEffect, useState } from 'react';
import ItemEmail from './ItemEmail';
import { useTranslation } from 'react-i18next';
import { cn } from '../../helpers/shadcnUtils.helper';

interface Iprops {
  showError: boolean;
  error: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  onEnter: (email: string) => void;
  deleteUser: (email: string) => void;
  disabled?: boolean;
  defaultEmails?: string[];
}

export const InputEmails = ({
  showError,
  error,
  name,
  value,
  onChange,
  onEnter,
  deleteUser,
  disabled,
  defaultEmails,
}: Iprops): JSX.Element => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const [valueInput, setValueInput] = useState('');
  const [displayErr, setDisplayErr] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  useEffect(() => {
    if (defaultEmails) {
      setEmails(defaultEmails);
    }
  }, [defaultEmails]);

  useEffect(() => {
    setErr(error);
    setDisplayErr(showError);
    setValueInput(value);
  }, [error, showError, value]);

  const deleteEmail = (email: string) => {
    if (disabled) return;
    const newEmails = emails.filter((em) => em !== email);
    setEmails([...newEmails]);
    deleteUser(email);
  };

  const addEmail = (emailInput: string) => {
    if (disabled) return;
    const emailReg =
      // eslint-disable-next-line no-useless-escape
      /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,9})$/;
    const emailValue: string = emailInput;
    if (emailValue === '' || !emailReg.test(emailValue)) {
      setDisplayErr(true);
      setErr(t('error.email'));
    } else {
      const existEmail = emails.find((email) => email === emailValue);
      if (existEmail) {
        setErr(t('validation.email.unique'));
        setDisplayErr(true);
      } else {
        if (!showError) {
          onEnter(emailValue);
          setEmails([...emails, emailValue]);
          setValueInput('');
        }
      }
    }
  };

  return (
    <div>
      <div className="w-full">
        <div
          className={cn(
            'peer relative w-full bg-neutral-98 rounded-xl focus-within:border focus-within:border-primary',
            displayErr && isTouched ? 'border-red-500' : '',
            emails.length > 0 ? 'border border-primary' : '',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          )}
        >
          <label
            htmlFor="input-emails"
            className={cn(
              'absolute left-3 pointer-events-none text-base text-neutral-40 transition-all duration-300  peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:!top-1 peer-focus:translate-y-0 peer-focus:scale-75',
              emails.length > 0 || valueInput
                ? 'top-1'
                : 'top-1/2 -translate-y-1/2 scale-100',
              disabled ? 'opacity-50' : '',
            )}
          >
            Email(s)
          </label>

          <div
            className={cn(
              'flex flex-wrap px-3 min-h-14',
              emails.length > 0 ? 'pt-7 pb-2' : 'pt-3 pb-2',
            )}
            id="input-emails"
          >
            {emails.map((email) => (
              <ItemEmail
                email={email}
                deleteEmail={deleteEmail}
                key={email}
                disabled={disabled}
              />
            ))}
            <input
              className={cn(
                "flex-1 text-base bg-transparent border-none outline-none",
                disabled ? 'cursor-not-allowed' : ''
              )}
              multiple
              name={name}
              value={valueInput}
              onChange={(e) => {
                if (!disabled) {
                  onChange(e);
                }
              }}
              type="email"
              disabled={disabled}
              onBlur={(e) => {
                if (!disabled) {
                  setIsTouched(true);
                  if (e.currentTarget.value !== '') {
                    addEmail(e.currentTarget.value);
                  }
                }
              }}
              onKeyPress={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ',')) {
                  e.stopPropagation();
                  e.preventDefault();
                  addEmail(e.currentTarget.value);
                }
              }}
            />
          </div>
        </div>

        {displayErr && isTouched ? (
          <p className="mt-1 ml-4 text-xs text-left text-red-00">{err}</p>
        ) : (
          <p className={cn(
            "mt-1 ml-4 text-xs text-neutral-40",
            disabled ? 'opacity-50' : ''
          )}>
            {t('placeholder.inviteUsers')}
          </p>
        )}
      </div>
    </div>
  );
};

export default InputEmails;
